import { Alert, Code } from "@one-nexty/components-web";

import { CodePane } from "../ui/preview";
import { Section, SubSection } from "../ui/section";

export function GettingStartedSection() {
  return (
    <Section
      id="demarrer"
      eyebrow="Guide"
      title="Installation"
      description="Trois étapes pour brancher une application sur le design system."
    >
      <SubSection
        title="1. Dépendances de workspace"
        hint="workspace:* lie le package local par lien symbolique"
      >
        <Frame>
          <CodePane label="apps/mon-app/package.json" code={`
{
  "dependencies": {
    "@one-nexty/components-web": "workspace:*",
    "@one-nexty/tokens": "workspace:*"
  },
  "devDependencies": {
    "@one-nexty/tailwind-config": "workspace:*"
  }
}`} />
        </Frame>
      </SubSection>

      <SubSection title="2. Importer le thème">
        <Frame>
          <CodePane label="src/styles.css" code={`
@import "tailwindcss";
@import "@one-nexty/tailwind-config/theme.css";

/* Tailwind v4 ne scanne pas node_modules : on lui indique
   explicitement où trouver les classes des composants. */
@source "../../../packages/components-web/src";`} />
        </Frame>
        <div className="mt-3">
          <Alert tone="warning" title="L'oubli le plus fréquent">
            Sans la ligne <Code>@source</Code>, les classes utilisées à l'intérieur des composants ne
            sont jamais générées : les boutons arrivent nus, et rien dans la console ne l'explique.
          </Alert>
        </div>
      </SubSection>

      <SubSection title="3. Consommer composants et tokens">
        <Frame>
          <CodePane label="src/app.tsx" code={`
import { Button, Card, CardBody, Field, Input } from "@one-nexty/components-web";
import { tokens } from "@one-nexty/tokens";

export function App() {
  return (
    <Card>
      <CardBody className="flex flex-col gap-4">
        {/* Classes issues du thème partagé */}
        <p className="text-fg-muted">bg-accent, text-fg, rounded-md…</p>

        <Field label="E-mail" required>
          <Input type="email" />
        </Field>

        {/* Et si besoin, la valeur brute d'un token */}
        <span style={{ color: tokens.color.primary[500] }}>oklch(58% 0.19 265)</span>

        <Button variant="primary">Action</Button>
      </CardBody>
    </Card>
  );
}`} />
        </Frame>
      </SubSection>

      <SubSection title="Les trois règles">
        <div className="grid gap-4 md:grid-cols-3">
          <Rule
            number="01"
            title="Une valeur visuelle vit dans les tokens"
            body={
              <>
                <Code>bg-[oklch(58%_0.19_265)]</Code> dans un composant, c'est une fuite hors du
                système.
              </>
            }
          />
          <Rule
            number="02"
            title="Les composants consomment le sémantique"
            body={
              <>
                <Code>bg-accent</Code> et non <Code>bg-primary-500</Code>. C'est ce qui rend le thème
                sombre et le rebranding gratuits.
              </>
            }
          />
          <Rule
            number="03"
            title="dist/ est généré, jamais édité"
            body={
              <>
                <Code>pnpm build</Code> régénère tout dans l'ordre topologique. Le dossier est
                gitignoré.
              </>
            }
          />
        </div>
      </SubSection>

      <SubSection title="Commandes">
        <Frame>
          <CodePane label="terminal" code={`
pnpm install          # installe et lie tous les packages

pnpm dev              # construit les tokens puis lance cette documentation
pnpm build            # construit tout, dans l'ordre topologique
pnpm typecheck        # vérifie les types de tous les packages

pnpm deploy           # build + firebase deploy --only hosting
pnpm deploy:preview   # canal de préproduction, expire en 7 jours

# cibler un package précis
pnpm --filter @one-nexty/tokens build
pnpm --filter @one-nexty/docs dev`} />
        </Frame>
      </SubSection>

      <SubSection
        title="Ajouter une plateforme"
        hint="@one-nexty/tokens ne dépend d'aucune plateforme ; tailwind-config n'est qu'un adaptateur"
      >
        <Frame>
          <CodePane label="react-native/theme.ts" code={`
import { lightTokens, darkTokens } from "@one-nexty/tokens/native";

const theme = colorScheme === "dark" ? darkTokens : lightTokens;

const styles = StyleSheet.create({
  button: {
    backgroundColor: theme["color-accent"],   // "#4170e9"
    borderRadius:    theme["radius-md"],      // 6
    paddingVertical: theme["spacing-2"],      // 8
  },
});`} />
        </Frame>
      </SubSection>
    </Section>
  );
}

/** Encadre un volet de code isolé, hors d'un `Preview`. */
function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-surface-raised">
      {children}
    </div>
  );
}

function Rule({
  number,
  title,
  body,
}: {
  number: string;
  title: string;
  body: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-border bg-surface-raised p-5">
      <span className="font-mono text-xs text-accent-fg">{number}</span>
      <h4 className="mt-1.5 text-sm font-semibold text-fg">{title}</h4>
      <p className="mt-1.5 text-sm text-fg-muted">{body}</p>
    </div>
  );
}
